import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../LanguageContext'; // Importamos el hook de idioma

const APYGroupCompany = () => {
  // Hook de idioma
  const { t } = useLanguage();
  
  // Estados para controlar animaciones y visibilidad
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [isVisible, setIsVisible] = useState({
    hero: false,
    quienes: false,
    objetivo: false,
    valores: false,
    equipo: false
  });
  
  // Referencias para las secciones observables
  const heroRef = useRef(null);
  const quienesRef = useRef(null);
  const objetivoRef = useRef(null);
  const valoresRef = useRef(null);
  const equipoRef = useRef(null);
  
  // Valores de la empresa con iconos mejorados
  const valores = [
    {
      title: t('company.values.transparency.title'),
      description: t('company.values.transparency.desc'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-12 h-12" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
          <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" />
        </svg>
      )
    },
    {
      title: t('company.values.trust.title'),
      description: t('company.values.trust.desc'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-12 h-12" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3z" />
          <path d="M10.95 15.55L8.5 13.1l-1.4 1.4 3.85 3.85 8.35-8.35-1.4-1.4-6.95 6.95z" />
        </svg>
      )
    },
    {
      title: t('company.values.closeness.title'),
      description: t('company.values.closeness.desc'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-12 h-12" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z" />
          <path d="M12 14c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
      )
    },
    {
      title: t('company.values.professionalism.title'),
      description: t('company.values.professionalism.desc'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-12 h-12" stroke="currentColor" strokeWidth="1.5">
          <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z" />
        </svg>
      )
    }
  ];
  
  // Miembros del equipo (ejemplo)
  const equipoMiembros = [
    {
      nombre: "ANTHONY YNAMINE",
      cargo: "CEO & Fundador",
      imagen: "/api/placeholder/300/300"
    },
    {
      nombre: "María Sato",
      cargo: "Directora Deportiva",
      imagen: "/api/placeholder/300/300"
    },
    {
      nombre: "Luis Nakamura",
      cargo: "Relaciones Internacionales",
      imagen: "/api/placeholder/300/300"
    }
  ];

  // Efecto para simular carga inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Efecto para la observación de elementos en pantalla
  useEffect(() => {
    const observerOptions = {
      threshold: 0.15,
      rootMargin: "0px 0px -100px 0px"
    };
    
    const handleIntersect = (entries) => {
      entries.forEach(entry => {
        const { id } = entry.target;
        
        if (entry.isIntersecting) {
          setIsVisible(prev => ({ ...prev, [id]: true }));
          setActiveSection(id);
        }
      });
    };
    
    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    
    const refs = [
      { ref: heroRef, id: 'hero' },
      { ref: quienesRef, id: 'quienes' },
      { ref: objetivoRef, id: 'objetivo' },
      { ref: valoresRef, id: 'valores' },
      { ref: equipoRef, id: 'equipo' }
    ];
    
    refs.forEach(({ ref, id }) => {
      if (ref.current) {
        ref.current.id = id;
        observer.observe(ref.current);
      }
    });
    
    return () => {
      refs.forEach(({ ref }) => {
        if (ref.current) observer.unobserve(ref.current);
      });
    };
  }, []);

  // Efecto de parallax para el fondo de la sección hero
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const heroSection = heroRef.current;
      
      if (heroSection) {
        const parallaxBg = heroSection.querySelector('.parallax-bg');
        if (parallaxBg) {
          parallaxBg.style.transform = `translateY(${scrollPosition * 0.4}px)`;
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="bg-black text-white min-h-screen overflow-hidden">
      {/* Hero Section Mejorada */}
      <section 
        ref={heroRef} 
        className="relative h-screen flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0 z-0 parallax-bg">
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/90 z-10"></div>
          <img 
            src="https://pub-8b7376b23b5a4b558c0089505b9b4b8e.r2.dev/oficina.png" 
            alt="Oficina AFY GROUP" 
            className="w-full h-full object-cover filter brightness-40"
          />
        </div>
        
        <div className="absolute inset-0 z-5">
          <div className="absolute left-0 top-0 w-full h-full overflow-hidden opacity-30">
            <div className="absolute -left-40 -top-40 w-80 h-80 bg-red-600 rounded-full filter blur-3xl"></div>
            <div className="absolute right-0 bottom-0 w-96 h-96 bg-red-600 rounded-full filter blur-3xl"></div>
          </div>
        </div>
        
        <div className={`relative z-10 container mx-auto px-6 text-center transform transition-all duration-1000 ease-out ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <h1 className="text-7xl md:text-8xl font-black mb-6 tracking-widest">
            {t('company.title')}
          </h1>
          <div className="w-24 h-1 bg-red-600 mx-auto mt-4 mb-8"></div>
          
          <p className="max-w-2xl mx-auto text-lg md:text-xl text-gray-300 mt-8 leading-relaxed">
            {t('company.subtitle')}
          </p>
          
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <a href="#quienes" className="px-8 py-3 bg-red-600 rounded-full text-white font-bold transition-all duration-300 hover:bg-red-700 hover:shadow-lg hover:shadow-red-600/20">
              {t('company.cta.know')}
            </a>
            <a href="#valores" className="px-8 py-3 border border-white/30 rounded-full text-white font-bold transition-all duration-300 hover:bg-white/10 hover:border-white">
              {t('company.cta.values')}
            </a>
          </div>
          
          <div className="absolute bottom-10 left-0 right-0 animate-bounce">
            <svg className="w-8 h-8 mx-auto text-white opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </div>
      </section>

      {/* Quiénes Somos Section Mejorada */}
      <section 
        ref={quienesRef} 
        className="py-24 md:py-32 relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-600 rounded-full filter blur-3xl opacity-5 transform translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-600 rounded-full filter blur-3xl opacity-5 transform -translate-x-1/2 translate-y-1/2"></div>
        
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div className={`transition-all duration-1000 ${isVisible.quienes ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <h2 className="text-4xl font-black mb-6">
                {t('company.who.title')}
                <div className="w-16 h-1 bg-red-600 mt-4"></div>
              </h2>
              
              <p className="text-lg leading-relaxed mb-8 text-gray-300">
                {t('company.who.desc1')}
              </p>
              
              <p className="text-lg leading-relaxed mb-8 text-gray-300">
                {t('company.who.desc2')}
              </p>
              
              <div className="mt-8">
                <div className="flex flex-wrap gap-4">
                  <div className="px-6 py-3 bg-gray-900 rounded-lg flex items-center">
                    <div className="mr-3 text-red-600">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    {t('company.feature.international')}
                  </div>
                  <div className="px-6 py-3 bg-gray-900 rounded-lg flex items-center">
                    <div className="mr-3 text-red-600">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    {t('company.feature.holistic')}
                  </div>
                  <div className="px-6 py-3 bg-gray-900 rounded-lg flex items-center">
                    <div className="mr-3 text-red-600">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    {t('company.feature.network')}
                  </div>
                </div>
              </div>
            </div>
            
            <div className={`relative transition-all duration-1000 delay-300 ${isVisible.quienes ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="relative h-[400px] md:h-[500px] rounded-lg overflow-hidden shadow-2xl shadow-red-600/10">
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent z-10"></div>
                <img 
                  src="https://pub-8b7376b23b5a4b558c0089505b9b4b8e.r2.dev/Imagen%20de%20WhatsApp%202025-03-28%20a%20las%2013.12.49_de491dcc.jpg" 
                  alt="AFY Group" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 w-full p-8 z-20">
                  <div className="bg-red-600 inline-block px-4 py-2 rounded-lg text-sm font-bold mb-3">
                    DESDE 2018
                  </div>
                  <h3 className="text-2xl font-bold">Impulsando talentos futbolísticos</h3>
                </div>
              </div>
              
              {/* Elemento decorativo posicionado fuera de la imagen principal */}
              <div className="absolute -top-8 -right-8 md:-top-10 md:-right-10 w-24 h-24 md:w-32 md:h-32 border-2 border-red-600 rounded-lg z-0"></div>
              <div className="absolute -bottom-8 -left-8 md:-bottom-10 md:-left-10 w-24 h-24 md:w-32 md:h-32 border-2 border-red-600 rounded-lg z-0"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Nuestro Objetivo Section Mejorada */}
      <section 
        ref={objetivoRef} 
        className="py-24 md:py-32 bg-gradient-to-br from-red-800 to-red-600 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-pattern opacity-10"></div>
        
        {/* Elementos de diseño para añadir profundidad */}
        <div className="absolute top-0 left-0 w-full h-6 bg-gradient-to-r from-black/40 to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-full h-6 bg-gradient-to-l from-black/40 to-transparent"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center mb-16">
              <div className={`md:w-1/4 mb-8 md:mb-0 transition-all duration-1000 ${isVisible.objetivo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center mx-auto">
                  <svg className="w-12 h-12 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 100-12 6 6 0 000 12z" />
                    <path d="M10 4a1 1 0 100 2 4 4 0 014 4 1 1 0 102 0 6 6 0 00-6-6z" />
                  </svg>
                </div>
              </div>
              
              <div className={`md:w-3/4 md:pl-8 transition-all duration-1000 delay-300 ${isVisible.objetivo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <h2 className="text-4xl font-black mb-8 text-center md:text-left">
                  {t('company.objective.title')}
                  <div className="w-16 h-1 bg-white mt-4 mx-auto md:mx-0"></div>
                </h2>
                
                <p className="text-lg leading-relaxed">
                  {t('company.objective.desc')}
                </p>
              </div>
            </div>
          </div>
          
          {/* Estadísticas o métricas */}
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-16 transition-all duration-1000 delay-600 ${isVisible.objetivo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="bg-white/10 backdrop-filter backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all duration-300">
              <div className="text-4xl font-bold mb-2">25+</div>
              <div className="text-lg">{t('company.stats.players')}</div>
            </div>
            <div className="bg-white/10 backdrop-filter backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all duration-300">
              <div className="text-4xl font-bold mb-2">10+</div>
              <div className="text-lg">{t('company.stats.countries')}</div>
            </div>
            <div className="bg-white/10 backdrop-filter backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all duration-300">
              <div className="text-4xl font-bold mb-2">95%</div>
              <div className="text-lg">{t('company.stats.satisfaction')}</div>
            </div>
          </div>
          
          {/* Elementos decorativos */}
          <div className={`absolute right-0 top-0 w-48 h-48 transition-all duration-1000 delay-500 ${isVisible.objetivo ? 'opacity-10 translate-x-0' : 'opacity-0 translate-x-20'}`}>
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-white">
              <path fill="currentColor" d="M49.8,-71.2C62.9,-62.1,71.4,-45.4,77.4,-27.5C83.4,-9.7,86.8,9.2,81.6,25.3C76.4,41.4,62.5,54.7,46.7,62.4C30.8,70.2,13.1,72.5,-4.8,78.5C-22.7,84.6,-45.4,94.5,-60,87.3C-74.5,80.1,-80.9,55.8,-83.4,33.7C-85.9,11.6,-84.4,-8.3,-76.2,-23.8C-68,-39.3,-53,-50.3,-38.1,-58.9C-23.3,-67.4,-8.6,-73.5,7.1,-83.5C22.8,-93.5,45.6,-107.3,55.9,-102.2C66.3,-97,64.3,-72.9,49.8,-71.2Z" transform="translate(100 100)" />
            </svg>
          </div>
          
          <div className={`absolute left-0 bottom-0 w-64 h-64 transition-all duration-1000 delay-700 ${isVisible.objetivo ? 'opacity-10 -translate-x-0' : 'opacity-0 -translate-x-20'}`}>
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-white">
              <path fill="currentColor" d="M44.7,-76.4C58.8,-69.2,71.9,-59.3,79.2,-45.8C86.6,-32.2,88.2,-15.1,85.9,0.8C83.6,16.7,77.5,31.3,68.2,44.2C58.9,57.1,46.4,68.2,31.6,73.5C16.8,78.8,-0.2,78.3,-17.9,76C-35.5,73.7,-53.8,69.5,-65.8,58.4C-77.8,47.3,-83.3,29.2,-83,11.9C-82.6,-5.5,-76.2,-23.1,-67.4,-38.7C-58.6,-54.3,-47.3,-67.9,-33.5,-75.3C-19.7,-82.7,-3.5,-83.8,7.4,-76.2C18.2,-68.5,30.6,-83.6,44.7,-76.4Z" transform="translate(100 100)" />
            </svg>
          </div>
        </div>
      </section>

      {/* Nuestros Valores Section Mejorada */}
      <section 
        ref={valoresRef} 
        className="py-24 md:py-32 relative overflow-hidden bg-gradient-to-b from-gray-950 to-black"
      >
        <div className="container mx-auto px-6">
          <h2 className={`text-4xl font-black text-center mb-20 relative transition-transform duration-1000 ${isVisible.valores ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {t('company.values.title')}
            <div className="w-16 h-1 bg-red-600 mx-auto mt-4"></div>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {valores.map((valor, index) => (
              <div 
                key={index}
                className={`bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-8 shadow-xl transition-all duration-1000 transform hover:-translate-y-2 hover:shadow-2xl hover:shadow-red-600/10 group ${isVisible.valores ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${300 + index * 200}ms` }}
              >
                <div className="w-16 h-16 rounded-lg bg-red-600/10 flex items-center justify-center mb-6 text-red-600 group-hover:bg-red-600 group-hover:text-white transition-all duration-300">
                  {valor.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{valor.title}</h3>
                <p className="text-gray-400">{valor.description}</p>
              </div>
            ))}
          </div>
          
          {/* Imagen o gráfico decorativo */}
          <div className={`mt-24 relative transition-all duration-1000 delay-800 ${isVisible.valores ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="w-full h-[300px] rounded-xl overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-black z-10"></div>
              <img 
                src="/api/placeholder/1200/300" 
                alt="AFY Group Values" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center z-20">
                <div className="text-center">
                  <h3 className="text-3xl font-bold mb-4">{t('company.philosophy.title')}</h3>
                  <p className="max-w-xl text-lg text-gray-300">
                    {t('company.philosophy.desc')}
                  </p>
                  <div className="mt-8">
                    <button className="px-10 py-4 bg-red-600 rounded-full text-white font-bold hover:bg-red-700 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-red-600/20">
                      {t('company.philosophy.cta')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Nuestro Equipo Section (Nueva) */}
<section 
  ref={equipoRef} 
  className="py-24 md:py-32 relative overflow-hidden"
>
  <div className="absolute inset-0 z-0 opacity-10">
    <div className="h-full w-full bg-pattern-grid"></div>
  </div>
  
  <div className="container mx-auto px-6 relative z-10">
    <h2 className={`text-4xl font-black text-center mb-20 relative transition-transform duration-1000 ${isVisible.equipo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
      {t('company.team.title')}
      <div className="w-16 h-1 bg-red-600 mx-auto mt-4"></div>
    </h2>
    
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {equipoMiembros.map((miembro, index) => (
        <div 
          key={index}
          className={`transition-all duration-1000 transform ${isVisible.equipo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          style={{ transitionDelay: `${300 + index * 200}ms` }}
        >
          <div className="group relative overflow-hidden rounded-xl">
            <div className="h-[400px] w-full">
              <img 
                src={miembro.imagen} 
                alt={miembro.nombre} 
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
              />
            </div>
            <div className="absolute bottom-0 left-0 w-full p-6 bg-gradient-to-t from-black to-transparent">
              <h3 className="text-xl font-bold">{miembro.nombre}</h3>
              <p className="text-red-500">{miembro.cargo}</p>
            </div>
            <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center p-6 transition-opacity duration-300">
              <p className="text-center text-white mb-4">
                Con más de 10 años de experiencia en el sector deportivo internacional.
              </p>
              <div className="flex space-x-3">
                <a href="#" className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center hover:bg-white hover:text-red-600 transition-colors duration-300">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm-1-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm5 7h-2v-3c0-.55-.45-1-1-1s-1 .45-1 1v3h-2v-6h2v1.1c.37-.72 1.2-1.1 1.84-1.1 1.5 0 2.16 1.12 2.16 2.5V17z"></path>
                  </svg>
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center hover:bg-white hover:text-red-600 transition-colors duration-300">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2a10 10 0 0 0-3.16 19.5c.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5A10 10 0 0 0 12 2z"></path>
                  </svg>
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center hover:bg-white hover:text-red-600 transition-colors duration-300">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"></path>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
    
    <div className={`mt-16 text-center transition-all duration-1000 delay-1000 ${isVisible.equipo ? 'opacity-100' : 'opacity-0'}`}>
      <a href="/contacto" className="inline-block px-10 py-4 bg-red-600 rounded-full text-white font-bold hover:bg-red-700 transition-all duration-300 shadow-lg shadow-red-600/20 hover:shadow-xl hover:shadow-red-600/30 transform hover:-translate-y-1">
        {t('company.team.join')}
      </a>
    </div>
  </div>
</section>

{/* Estilos CSS para animaciones y patrones */}
<style jsx>{`
  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-15px); }
  }
  
  .bg-pattern {
    background-image: linear-gradient(30deg, #000000 12%, transparent 12.5%, transparent 87%, #000000 87.5%, #000000),
    linear-gradient(150deg, #000000 12%, transparent 12.5%, transparent 87%, #000000 87.5%, #000000),
    linear-gradient(30deg, #000000 12%, transparent 12.5%, transparent 87%, #000000 87.5%, #000000),
    linear-gradient(150deg, #000000 12%, transparent 12.5%, transparent 87%, #000000 87.5%, #000000),
    linear-gradient(60deg, #00000077 25%, transparent 25.5%, transparent 75%, #00000077 75%, #00000077),
    linear-gradient(60deg, #00000077 25%, transparent 25.5%, transparent 75%, #00000077 75%, #00000077);
    background-size: 80px 140px;
    background-position: 0 0, 0 0, 40px 70px, 40px 70px, 0 0, 40px 70px;
  }
  
  .bg-pattern-grid {
    background-size: 30px 30px;
    background-image:
      linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  }
`}</style>
</div>
);
};

export default APYGroupCompany;